import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  Token,
  ActionPerformed,
} from '@capacitor/push-notifications';
import { filter, take } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { SessionService } from './session.service';
import { NotificationsApiService } from './notifications-api.service';

/**
 * Registers FCM/APNs device tokens with the API on native builds only.
 * Web push continues to use VAPID via {@link PushNotificationService}.
 */
@Injectable({ providedIn: 'root' })
export class NativePushService {
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly notificationsApi = inject(NotificationsApiService);
  private started = false;
  private lastSentToken: string | null = null;

  /** Call on logout so the next session can register push again. */
  resetForNextSession(): void {
    this.started = false;
    this.lastSentToken = null;
  }

  /** Call once from the mobile shell after the app is bootstrapped. */
  startWhenReady(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        filter((e) => e.urlAfterRedirects.includes('/tabs')),
        take(1),
      )
      .subscribe(() => void this.registerNativePush());
  }

  private async registerNativePush(): Promise<void> {
    for (let i = 0; i < 80; i++) {
      if (this.session.getUserWorkspace()?.uid) {
        break;
      }
      await new Promise((r) => setTimeout(r, 250));
    }
    if (!this.session.getUserWorkspace()?.uid) {
      return;
    }

    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') {
      return;
    }

    await PushNotifications.addListener('registration', (t: Token) => {
      void this.sendTokenToServer(t.value);
    });

    await PushNotifications.addListener('registrationError', () => {
      // No-op; user can retry on next app launch
    });

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (_: ActionPerformed) => {
        // Deep-link handling can be added later (open chats from notification tap)
      },
    );

    await PushNotifications.register();
  }

  private async sendTokenToServer(token: string): Promise<void> {
    if (!token || token === this.lastSentToken) {
      return;
    }
    if (!this.session.getUserWorkspace()?.uid) {
      return;
    }
    const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
    try {
      await firstValueFrom(
        this.notificationsApi.saveDevicePushToken(platform, token),
      );
      this.lastSentToken = token;
    } catch {
      // Retry on next registration event
    }
  }
}
